import React, { useEffect, useState } from 'react';
import { theme } from 'antd';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { RootState } from 'store/configureStore';
import TitleBar from 'components/TitleBar';
import GoBackButton from 'components/GoBackButton';
import ApiService from 'utils/ApiCalls';
import { DependencyTree } from '../DependencyTree';
import { renderProcessInventoryNode } from './renderNode';
import { processInventoryLevels } from 'constants/businessProcessInventoryLevels';
import * as Styled from '../../Layouts/Layout.styled';
import * as LocalStyled from 'pages/Reporting/ControlReport/ControlReport.styled';
import { useTranslation } from 'react-i18next';

const { useToken } = theme;

const BusinessProcessReport = () => {
  const { token } = useToken();
  const { t } = useTranslation();
  const { scheme } = useSelector((state: RootState) => state.global);

  const [reportData, setReportData] = useState<any[]>([]);

  const [shouldFetch, setShouldFetch] = useState(false);

  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);

  const [filters, setFilters] = useState<{
    group_ids?: string;
    division_ids?: string;
    region_ids?: string;
    legal_entity_ids?: string;
    business_unit_ids?: string;
  }>({});

  const [groupOptions, setGroupOptions] = useState<any[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<any[]>([]);
  const [regionOptions, setRegionOptions] = useState<any[]>([]);
  const [legalEntityOptions, setLegalEntityOptions] = useState<any[]>([]);
  const [businessUnitOptions, setBusinessUnitOptions] = useState<any[]>([]);

  // dropdown data

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [groups, divisions, regions, legalEntities, businessUnits] =
          await Promise.all([
            ApiService.getGroupBPR(),
            ApiService.getDivisionBPR(),
            ApiService.getRegionBPR(),
            ApiService.getLegalEntitiesBPR(),
            ApiService.getBusinessUnitBPR()
          ]);

        setGroupOptions(
          groups.data.data.map((x: any) => ({
            label: x.name,
            value: x.id
          }))
        );

        setDivisionOptions(
          divisions.data.data.map((x: any) => ({
            label: x.name,
            value: x.id
          }))
        );

        setRegionOptions(
          regions.data.data.map((x: any) => ({
            label: x.name,
            value: x.id
          }))
        );

        setLegalEntityOptions(
          legalEntities.data.data.map((x: any) => ({
            label: x.name,
            value: x.id
          }))
        );

        setBusinessUnitOptions(
          businessUnits.data.data.map((x: any) => ({
            label: x.name,
            value: x.id
          }))
        );
      } catch (error) {
        console.error(error);
      }
    };

    fetchDropdowns();
  }, []);

  // report api

  useEffect(() => {
    if (!shouldFetch) return;

    const fetchReport = async () => {
      try {
        const params: string[] = [];

        if (filters.group_ids) params.push(`group_ids=${filters.group_ids}`);

        if (filters.division_ids)
          params.push(`division_ids=${filters.division_ids}`);

        if (filters.region_ids) params.push(`region_ids=${filters.region_ids}`);

        if (filters.legal_entity_ids)
          params.push(`legal_entity_ids=${filters.legal_entity_ids}`);

        if (filters.business_unit_ids)
          params.push(`business_unit_ids=${filters.business_unit_ids}`);

        if (selectedDate)
          params.push(`date=${selectedDate.format('YYYY-MM-DD')}`);

        const response = await ApiService.getProcessInventory(
          params.length ? params.join('&') : undefined
        );
        setReportData(response.data?.data ?? []);
      } catch (error) {
        console.error(error);

        setReportData([]);
      } finally {
        setShouldFetch(false);
      }
    };

    fetchReport();
  }, [shouldFetch, filters, selectedDate]);

  const renderNode = (node: any, level: number, tkn: any) =>
    renderProcessInventoryNode(node, level, tkn, t);

  return (
    <Styled.StyledLayout token={token}>
      <TitleBar
        title={t('PROCESSES')}
        goBackButtons={
          <GoBackButton to='/dependency_bpr' text={t('GO_BACK')} />
        }
      />
      <Styled.HorizontalLayoutMain theme={scheme}>
        <Styled.Container>
          <LocalStyled.HierarchyLevels>
            <LocalStyled.EntitySelect
              mode='multiple'
              placeholder={t('GROUP')}
              options={groupOptions}
              onChange={(value) => {
                const selected = value as number[];

                setFilters({
                  ...filters,
                  group_ids: selected.length ? selected.join(',') : undefined
                });
              }}
              allowClear
              popupMatchSelectWidth={false}
              getPopupContainer={(trigger) =>
                trigger.parentElement || document.body
              }
            />

            <LocalStyled.EntitySelect
              mode='multiple'
              placeholder={t('DIVISION')}
              options={divisionOptions}
              onChange={(value) => {
                const selected = value as number[];

                setFilters({
                  ...filters,
                  division_ids: selected.length ? selected.join(',') : undefined
                });
              }}
              allowClear
              popupMatchSelectWidth={false}
              getPopupContainer={(trigger) =>
                trigger.parentElement || document.body
              }
            />

            <LocalStyled.EntitySelect
              mode='multiple'
              placeholder={t('REGION')}
              options={regionOptions}
              onChange={(value) => {
                const selected = value as number[];

                setFilters({
                  ...filters,
                  region_ids: selected.length ? selected.join(',') : undefined
                });
              }}
              allowClear
              popupMatchSelectWidth={false}
              getPopupContainer={(trigger) =>
                trigger.parentElement || document.body
              }
            />

            <LocalStyled.EntitySelect
              mode='multiple'
              placeholder={t('LEGALENTITY')}
              options={legalEntityOptions}
              onChange={(value) => {
                const selected = value as number[];

                setFilters({
                  ...filters,
                  legal_entity_ids: selected.length
                    ? selected.join(',')
                    : undefined
                });
              }}
              allowClear
              popupMatchSelectWidth={false}
              getPopupContainer={(trigger) =>
                trigger.parentElement || document.body
              }
            />

            <LocalStyled.EntitySelect
              mode='multiple'
              placeholder={t('BUSINESSUNIT')}
              options={businessUnitOptions}
              onChange={(value) => {
                const selected = value as number[];

                setFilters({
                  ...filters,
                  business_unit_ids: selected.length
                    ? selected.join(',')
                    : undefined
                });
              }}
              allowClear
              popupMatchSelectWidth={false}
              getPopupContainer={(trigger) =>
                trigger.parentElement || document.body
              }
            />
            <Styled.StyledButton
              type='primary'
              onClick={() => setShouldFetch(true)}
              className='display-save-button'
            >
              {t('DISPLAY')}
            </Styled.StyledButton>
          </LocalStyled.HierarchyLevels>

          <Styled.ContainerDependency token={token}>
            <Styled.CardItemWrapper>
              {reportData.length === 0 ? (
                <LocalStyled.EmptyState>
                  {t('common.noData')}
                </LocalStyled.EmptyState>
              ) : (
                <DependencyTree
                  data={reportData}
                  levels={processInventoryLevels}
                  renderNode={renderNode}
                />
              )}
            </Styled.CardItemWrapper>
          </Styled.ContainerDependency>
        </Styled.Container>
      </Styled.HorizontalLayoutMain>
    </Styled.StyledLayout>
  );
};

export default BusinessProcessReport;
